import { api } from '../../lib/api';

/**
 * The gradient behind a profile header.
 *
 * Mirrors `App\Domain\User\BannerPresets`, which is the authority: it holds the
 * list, it validates a choice, and it derives one from the username when
 * nobody has made a choice - so there is no state in which a header has nothing
 * behind it.
 *
 * The colours come from the server rather than living in a stylesheet here,
 * because the picker draws swatches and the profile paints a header, and two
 * hand-maintained copies of eight gradients is a thing that drifts.
 */
export interface BannerGradient {
  preset: string;
  /** Dark to light. Painted in order along `angle`. */
  stops: string[];
  angle: number;
  /**
   * Whether this was derived from the username rather than chosen.
   *
   * The picker shows nothing as selected when it is, rather than lying that
   * somebody picked the one they were given - a difference that matters the
   * moment they pick that same one deliberately.
   */
  derived: boolean;
}

export interface BannerPreset {
  preset: string;
  label: string;
  stops: string[];
  angle: number;
}

/** The CSS for a gradient, wherever one is painted. */
export function gradientCss(gradient: { stops: string[]; angle: number }): string {
  return `linear-gradient(${gradient.angle}deg, ${gradient.stops.join(', ')})`;
}

/**
 * A colour with an alpha, from one of the server's hex stops.
 *
 * Written out rather than reached for via `color-mix`, because these end up
 * layered over a near-black base and the arithmetic should be visible in the
 * one place the values are chosen.
 */
function rgba(hex: string, alpha: number): string {
  const value = Number.parseInt(hex.replace('#', ''), 16);

  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}

/**
 * The wash behind a live room's now-playing column.
 *
 * ## Why the header decides it
 *
 * A broadcast lives on its host's profile, and the header is the one thing on
 * that page the host actually chose. Letting the room underneath it pick up the
 * same colour is the difference between a broadcast that looks like theirs and
 * one that looks like everybody's.
 *
 * ## But muted, because text sits on it
 *
 * The mockup's wash is a near-black radial with a faint glow at the top left,
 * and the status line and track title sit right in that glow. So the preset's
 * colours arrive at low alpha over the same near-black rather than replacing
 * it: the room is tinted, not repainted.
 */
export function ambientCss(gradient: { stops: string[] }): string {
  /*
   * Darkest first, lightest last - the order `BannerPresets` writes them in.
   * Defaulted rather than asserted so a preset with two stops still paints
   * something, which is the difference between a duller room and a blank one.
   */
  // Neutral, because a missing stop is the absence of a chosen colour rather
  // than a colour. The navy that used to be here tinted a whole room on the one
  // path where the server had sent nothing to tint it with.
  const [dark = '#0a0a0e', mid = dark, light = mid] = gradient.stops;

  /*
   * The base layer is the app's own ground, so it follows the theme.
   *
   * It used to be three literals - `#16162e`, `#0a0a16`, `#08080f` - which was
   * the dark palette of the day baked into a file that is otherwise about
   * content. When the chrome went neutral this stayed navy, and a room was the
   * one place on the platform still painted in the old colours.
   *
   * The host's stops above are content and stay literal; this is not. Custom
   * properties resolve in an inline `style`, so the tokens work here exactly as
   * they do in a stylesheet.
   */
  return [
    `radial-gradient(120% 85% at 18% 0%, ${rgba(light, 0.46)} 0%, ${rgba(mid, 0.52)} 24%,`
      + ` ${rgba(dark, 0.7)} 52%, transparent 80%)`,
    'radial-gradient(120% 80% at 18% 0%, var(--color-slate-2) 0%,'
      + ' var(--color-deep) 52%, var(--color-void) 100%)',
  ].join(', ');
}

export const banners = {
  presets: () => api.get<{ presets: BannerPreset[] }>('/profile/banner-presets'),
  choose: (preset: string | null) => api.patch('/me', { banner_preset: preset }),
  remove: () => api.delete<{ removed: boolean }>('/me/banner'),
};
